// Placeholder for entitlementStore - will be populated from UI_ARCHITECTURE.md
import { create } from 'zustand'
import { UserSession, EntitlementState, UserRole, SeatType, PremiumModule, FeatureFlags } from '../types/entitlements'
import { authService } from '../services/authService'

const getDefaultFeatureFlags = (role: UserRole, seatType: SeatType): FeatureFlags => {
  const baseFlags: FeatureFlags = {
    spurDashboard: false,
    switchListGeneration: false,
    sweepWorkGeneration: false,
    blockTrainBuilding: false,
    inboundManifestReceiver: false,
    kveSimulationWindows: false,
    switchListAction: false,
    trackInventoryCheck: false,
    adHocTrackOverride: false,
    mechanicalBadOrderForm: false,
    blueFlagToggle: false,
    tagScanningExecution: false,
    nativeIntentListeners: false,
    assignedSpurAccess: false,
    readyToPullMarking: false,
    partnerDataView: false,
    foulZoneOverlay: false,
    carbonReporting: false,
    carbonDataExport: false,
    transloadCore: false,
    transloadAutomation: false,
    transloadAuditTrail: false,
  }

  if (role === UserRole.YARDMASTER && seatType === SeatType.DESKTOP) {
    return {
      ...baseFlags,
      spurDashboard: true,
      switchListGeneration: true,
      sweepWorkGeneration: true,
      blockTrainBuilding: true,
      inboundManifestReceiver: true,
      kveSimulationWindows: true,
    }
  }

  if (role === UserRole.CONDUCTOR && seatType === SeatType.HANDHELD) {
    return {
      ...baseFlags,
      switchListAction: true,
      trackInventoryCheck: true,
      adHocTrackOverride: true,
      mechanicalBadOrderForm: true,
      blueFlagToggle: true,
      tagScanningExecution: true,
      nativeIntentListeners: true,
    }
  }

  if (role === UserRole.PARTNER && seatType === SeatType.PORTAL) {
    return {
      ...baseFlags,
      assignedSpurAccess: true,
      readyToPullMarking: true,
      partnerDataView: true,
    }
  }

  if (role === UserRole.TRANSLOAD_OPERATOR && seatType === SeatType.TABLET) {
    return {
      ...baseFlags,
      transloadCore: true,
    }
  }

  return baseFlags
}

const applyPremiumModules = (flags: FeatureFlags, modules: PremiumModule[]): FeatureFlags => {
  const updatedFlags = { ...flags }

  if (modules.includes(PremiumModule.SAFETY_EXPANSION)) {
    updatedFlags.foulZoneOverlay = true
  }

  if (modules.includes(PremiumModule.CARBON_REPORTING)) {
    updatedFlags.carbonReporting = true
    updatedFlags.carbonDataExport = true
  }

  if (modules.includes(PremiumModule.TRANSLOAD_AUTOMATION)) {
    updatedFlags.transloadAutomation = true
    updatedFlags.transloadAuditTrail = true
  }

  return updatedFlags
}

export const useEntitlementStore = create<EntitlementState>((set, get) => ({
  session: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  setSession: (session: UserSession) => {
    set({
      session,
      isAuthenticated: true,
      error: null,
    })
  },

  clearSession: () => {
    set({
      session: null,
      isAuthenticated: false,
      error: null,
    })
  },

  refreshEntitlements: async () => {
    const { session } = get()
    if (!session) return

    set({ isLoading: true, error: null })

    try {
      const response = await fetch('/api/v1/auth/entitlements', {
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to refresh entitlements')
      }

      const data = await response.json()
      
      const baseFlags = getDefaultFeatureFlags(data.role, data.seatType)
      const updatedFlags = applyPremiumModules(baseFlags, data.premiumModules)

      set({
        session: {
          ...data,
          featureFlags: updatedFlags,
        },
        isLoading: false,
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Unknown error',
        isLoading: false,
      })
    }
  },

  hasFeature: (feature: keyof FeatureFlags) => {
    const { session } = get()
    return session?.featureFlags[feature] ?? false
  },

  hasModule: (module: PremiumModule) => {
    const { session } = get()
    return session?.premiumModules.includes(module) ?? false
  },

  canAccessSpur: (spurId: string) => {
    const { session } = get()
    
    if (session?.role === UserRole.YARDMASTER) {
      return true
    }

    if (session?.role === UserRole.PARTNER) {
      return session.assignedSpurs.includes(spurId)
    }

    return true
  },
}))
